import { Button, FormControl, FormHelperText, FormLabel, Grid, Input, Stack, Typography } from '@mui/joy';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { Column, ColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import type { FunctionComponent } from 'react';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { BackendTable } from '@/components/backend/table/BackendTable';
import { AdminLevelTypeFilter } from '@/components/backend/table/filters/AdminLevelTypeFilter';
import { AdminLevelUserRoleFilter } from '@/components/backend/table/filters/AdminLevelUserRoleFilter';
import { AdminLevelUserStatusFilter } from '@/components/backend/table/filters/AdminLevelUserStatusFilter';
import { ErrorAlert } from '@/components/errors/ErrorAlert';
import { FloatingInput } from '@/components/form-elements/FloatingInput';
import { getI18nRadioLabel } from '@/components/form-elements/I18nRadioLabel';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { ButtonLink } from '@/components/router/ButtonLink';
import { I18N_DE } from '@/i18n/de';
import { EMAIL_PATTERN } from '@/modules/EMAIL_PATTERN';
import type { AdminLevel, AdminLevelUser, UpdateUserDto, User } from '@/modules/api';
import { UserRole, UserStatus } from '@/modules/api';
import { backendApiUsersFindOneOptions, backendApiUsersUpdateMutation } from '@/modules/api/@tanstack/react-query.gen';
import { exactArraySelectionFilter } from '@/utils/exactArraySelectionFilter';
import { i18nSortingFn } from '@/utils/i18nSortingFn';

export const Route = createFileRoute('/verwaltung/_global/benutzerinnen/$userId/')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

const columnHelper = createColumnHelper<AdminLevelUser>();

const columns = [
  columnHelper.accessor('adminLevel.name', {
    header: 'Name',
    enableColumnFilter: false,
  }),
  columnHelper.accessor('adminLevel.type', {
    id: 'adminLevelType',
    header: 'Ebene',
    filterFn: exactArraySelectionFilter<AdminLevel['type'], AdminLevelUser>,
    sortingFn: i18nSortingFn(I18N_DE.AdminLevelType),
    cell: (info) => I18N_DE.AdminLevelType[info.getValue()],
  }),
  columnHelper.accessor('role', {
    header: 'Rolle',
    filterFn: exactArraySelectionFilter<AdminLevelUser['role'], AdminLevelUser>,
    sortingFn: i18nSortingFn(I18N_DE.AdminLevelUserRole),
    cell: (info) => I18N_DE.AdminLevelUserRole[info.getValue()],
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    enableGlobalFilter: false,
    filterFn: exactArraySelectionFilter<AdminLevelUser['status'], AdminLevelUser>,
    sortingFn: i18nSortingFn(I18N_DE.AdminLevelUserStatus),
    cell: (info) => I18N_DE.AdminLevelUserStatus[info.getValue()],
  }),
] as ColumnDef<AdminLevelUser>[];

const AdminLevelUserTypeFilter: FunctionComponent<{ column: Column<AdminLevelUser> }> = AdminLevelTypeFilter;

type FormData = Pick<UpdateUserDto, 'role' | 'status' | 'email'>;

const compareUserData = (user: User, formData: FormData) =>
  formData.role === user.role && formData.status === user.status && formData.email === user.email;

function RouteComponent() {
  const params = Route.useParams();

  const {
    data: user,
    isFetching,
    error,
  } = useSuspenseQuery(backendApiUsersFindOneOptions({ path: { userId: params.userId } }));

  const { mutateAsync, error: mutateError } = useMutation(backendApiUsersUpdateMutation());

  const {
    control,
    register,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: useMemo(
      () => ({
        role: user?.role,
        status: user?.status,
        email: user?.email,
      }),
      [user],
    ),
  });

  useEffect(
    () =>
      reset({
        role: user?.role,
        status: user?.status,
        email: user?.email,
      }),
    [user, reset],
  );

  if (error) {
    return <ErrorAlert error={error} />;
  }

  if (!user) {
    return null;
  }

  return (
    <Grid container spacing={8}>
      <Grid md={6}>
        <Stack
          component="form"
          gap={2}
          onSubmit={handleSubmit(async (data) =>
            mutateAsync({
              path: {
                userId: params.userId,
              },
              body: data,
            }),
          )}
        >
          <Typography level="h3">Profil</Typography>
          <FormControl>
            <FormLabel>Vorname</FormLabel>
            <Input readOnly value={user.firstName} />
          </FormControl>
          <FormControl>
            <FormLabel>Nachname</FormLabel>
            <Input readOnly value={user.lastName} />
          </FormControl>
          <FormControl error={!!errors.email}>
            <FormLabel>E-Mail</FormLabel>
            <Input
              slotProps={{
                input: {
                  ...register('email', { required: true, pattern: EMAIL_PATTERN }),
                  label: 'E-Mail Adresse',
                },
              }}
              slots={{
                input: FloatingInput,
              }}
            />
            {errors.email ? <FormHelperText>{errors.email.message}</FormHelperText> : null}
          </FormControl>
          <FormControl>
            <FormLabel>Rolle</FormLabel>
            <Controller
              control={control}
              defaultValue={user.role}
              name="role"
              render={({ field }) => (
                <RadioWithDescriptionGroup
                  {...field}
                  options={Object.values(UserRole)}
                  optionLabel={getI18nRadioLabel({
                    i18nTitle: I18N_DE.UserRole,
                    i18nDescription: I18N_DE.UserRoleDescription,
                  })}
                />
              )}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Status</FormLabel>
            <Controller
              control={control}
              defaultValue={user.status}
              name="status"
              render={({ field }) => (
                <RadioWithDescriptionGroup
                  {...field}
                  disabledOptions={user?.status !== UserStatus.REGISTERED ? [UserStatus.REGISTERED] : undefined}
                  options={Object.values(UserStatus)}
                  optionLabel={getI18nRadioLabel({
                    i18nTitle: I18N_DE.UserStatus,
                    i18nDescription: I18N_DE.UserStatusDescription,
                  })}
                />
              )}
            />
          </FormControl>
          <Stack direction="row" gap={2} justifyContent="flex-end" marginTop={4}>
            {mutateError ? <FormHelperText>{mutateError.message}</FormHelperText> : null}
            <Button disabled={compareUserData(user, watch())} type="submit">
              Speichern
            </Button>
          </Stack>
        </Stack>
      </Grid>

      <Grid md={6}>
        <Stack direction="row" gap={2} justifyContent="space-between" marginBottom={4}>
          <Typography level="h3">Zugeordnete Verwaltungsebenen</Typography>

          <ButtonLink
            params={{ userId: params.userId }}
            to="/verwaltung/benutzerinnen/$userId/verwaltungsebenen/hinzufuegen"
          >
            Hinzufügen
          </ButtonLink>
        </Stack>

        <BackendTable
          columns={columns}
          data={user.adminLevels}
          getRowId={(row) => `${row.adminLevelId}-${row.userId}`}
          isFetching={isFetching}
          onClickNavigate={(row) => `/verwaltung/${row.original.adminLevelId}/benutzerinnen/${row.original.userId}`}
          searchInputPlaceholder="Suche nach Verwaltungsebene"
          subject="Verwaltungsebenen"
          filters={{
            adminLevelType: AdminLevelUserTypeFilter,
            role: AdminLevelUserRoleFilter,
            status: AdminLevelUserStatusFilter,
          }}
        />
      </Grid>
    </Grid>
  );
}
